import {
  resolveDataField,
  isRaceColorMode,
  isTrainingRaceMetadataValue,
  RACE_NUMBER_COLLAPSED_LEGEND_NAME,
} from "./colorScale";

export type PerformanceColorGroup = { name: string; color: string };

export type PerformancePointColorOptions = {
  colorField: string;
  groups: PerformanceColorGroup[];
  colorScale?: (source: string) => string;
};

/**
 * Resolve scatter/legend color for a performance aggregate row (matches AdvancedScatter getColor).
 */
export function getPerformanceAggregateColor(
  row: Record<string, unknown>,
  options: PerformancePointColorOptions
): string {
  const { colorField, groups, colorScale } = options;

  if (colorField === "SOURCE_NAME" && colorScale && typeof colorScale === "function") {
    const sourceName =
      row.source_name ?? row.sourceName ?? row.source ?? row.SOURCE ?? row.SOURCE_NAME;
    if (sourceName) {
      const normalizedSource = String(sourceName).toLowerCase();
      try {
        const color = colorScale(normalizedSource);
        if (color && typeof color === "string" && color !== "undefined" && color !== "null") {
          return color;
        }
      } catch {
        // fall through to groups / default
      }
    }
  }

  if (colorField && Array.isArray(groups) && groups.length > 0) {
    let value: unknown;
    if (colorField === "STATE") {
      value =
        row.state ?? row.State ?? row.STATE ?? resolveDataField(row, "state") ?? resolveDataField(row, "STATE");
    } else {
      value = resolveDataField(row, colorField);
    }

    if (isRaceColorMode(colorField) && (value === -1 || value === "-1")) {
      value = "TRAINING";
    }

    if (isRaceColorMode(colorField) && groups) {
      const hasRacingBucket = groups.some((g) => String(g.name) === RACE_NUMBER_COLLAPSED_LEGEND_NAME);
      if (hasRacingBucket) {
        if (isTrainingRaceMetadataValue(value)) {
          const tg = groups.find((g) => String(g.name) === "TRAINING");
          return tg ? tg.color : "lightgrey";
        }
        const rg = groups.find((g) => String(g.name) === RACE_NUMBER_COLLAPSED_LEGEND_NAME);
        return rg ? rg.color : "lightgrey";
      }
    }

    if (colorField === "LEG") {
      const note = value !== undefined && value !== null ? String(value) : "";
      if (note === "TRAINING" || note === "training" || note === "-1" || value === -1) {
        return "lightgrey";
      }
    }

    const valueStr = value !== undefined && value !== null ? String(value) : null;
    let group: PerformanceColorGroup | undefined;
    if (valueStr !== null) {
      group = groups.find((g) => {
        if (colorField === "STATE") {
          return String(g.name).toLowerCase() === valueStr.toLowerCase();
        }
        if (String(g.name) === valueStr) return true;
        const groupNum = Number(g.name);
        const valueNum = Number(valueStr);
        if (!Number.isNaN(groupNum) && !Number.isNaN(valueNum) && groupNum === valueNum) return true;
        if (String(g.name).toLowerCase() === valueStr.toLowerCase()) return true;
        return false;
      });
    }

    return group ? group.color : "lightgrey";
  }

  return "lightgrey";
}
